import {
  AdjacentOOSItemStatus,
  SnoozedItemsData,
} from '../types/SubstituteItemDetails';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  timerCounter,
  timerStringInDigits,
} from '../components/ItemDetail/Counter/counter';
import {
  useAdjacentOOSItemFound,
  useGetAdjacentOOSItem,
  useGetConstants,
  useGetRunForItemDetail,
  useSubstituteItemDetailsReducer,
} from '../hooks/SubstituteItemDetail';

import {APIV1Version} from '@buncha/common/types/common';
import {BottomButton} from '@buncha/components/composites/Button';
import {Box} from '../../components/core/view';
import CustomerResponseSheet from '../components/ItemDetail/CustomerResponseSheet';
import DropAdjacentItemModal from '../components/ItemDetail/DropAdjacentItemModal/DropAdjacentItemModal';
import Header from '../../components/composites/Header/Header';
import If from '../../components/conditional/If';
import {ItemDetailsScreen} from '../navigation/types';
import LocationInStore from '../components/ItemDetail/locationInStore';
import {Modules} from '@buncha/utils/types';
import QRCode from '@buncha/components/core/icon/QRCode';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {StorageKeys} from '@buncha/config/storage/type';
import SubstituteDetails from '../components/SubstituteDetails';
import SubstituteItemDetailsBody from '../components/ItemDetail/SubstituteItemDetailsBody';
import {SubstituteItemDetailsContext} from '../context/SubstituteItemDetails';
import {homeActions} from '@buncha/home/reducer';
import {isWeb} from '@buncha/utils/common';
import moment from 'moment';
import {navigation} from '@buncha/config/navigation';
import {storage} from '@buncha/config/storage';
import {styles} from '../styles/ItemDetails';
import {useAppDispatch} from '@buncha/config/redux/hooks';
import {useColorThemeType} from '../../appStyles/hooks/theme';
import {useDisableBack} from '@buncha/common/hooks/common';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface RouteParamsType {
  storeIndex?: string;
  catalogItemId?: number;
  orderItemId?: number;
  runId?: number;
  adjacentOOSId?: number;
}

const SubstituteItemDetailsPage = () => {
  const theme = useColorThemeType();
  const [state, dispatch] = useSubstituteItemDetailsReducer();
  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);
  const {getConstants, constantsLoading} = useGetConstants(dispatch);
  const {adjacentOOSItemFound} = useAdjacentOOSItemFound();
  const [getRunForItemDetail, runState] = useGetRunForItemDetail(dispatch);
  const {getAdjacentOOSItem} = useGetAdjacentOOSItem(dispatch);
  const {button} =
    useGetLocale(
      Modules.itemDetails,
      ItemDetailsScreen.SubstituteItemDetailPage,
    ) ?? {};

  const route = useRoute();
  useDisableBack();
  const appDispatch = useAppDispatch();
  const routeParams: RouteParamsType | undefined = route?.params;
  const orderItemId = Number(routeParams?.orderItemId) || 0;
  const storeIndex = routeParams?.storeIndex;
  const runId = routeParams?.runId;
  const storeMasterId = runState?.data?.store?.storeMasterId;
  const newQuantity = state?.item?.quantity;
  const oldQuantity = state?.item?.substitutedBy?.quantity;

  const [loadingData, setLoadingData] = useState(true);
  const [loadingItemFound, setLoadingItemFound] = useState(false);
  const [showDropModal, setShowDropModal] = useState(false);
  const [snoozedTimerItemsData, setSnoozedTimerItemsData] =
    useState<SnoozedItemsData>({});

  const quantity =
    state.item?.totalupdatedQuantityByShopper ?? state.item?.quantity;

  const conversationId = state.buyersForItem?.[0]?.conversationId;

  const bottomButtonLoading = loadingData || constantsLoading;
  const isFound = state?.foundAt !== undefined;
  const isRequestSentToBuyer =
    state?.itemStatus === AdjacentOOSItemStatus.requestSentToBuyer;
  const isRequestSentToShopper =
    state?.itemStatus === AdjacentOOSItemStatus.requestedToShopper;
  const isRequestAcceptedByBuyer =
    state?.itemStatus === AdjacentOOSItemStatus.requestAcceptedByBuyer;

  const date = moment(state?.foundAt);
  const initDaysToExpire = date.diff(moment());
  const initEventDate = moment.duration(initDaysToExpire);
  const isTimedOut = date ? moment().isAfter(date) : false;

  const [timer, setTimer] = useState({
    days: initEventDate.months() * 31 + initEventDate.days(),
    hours: initEventDate.hours(),
    mins: initEventDate.minutes(),
    secs: initEventDate.seconds(),
  });

  const timerFunc = useCallback(() => {
    const {days, hours, mins, secs} = timerCounter(date);
    setTimer({
      days,
      hours,
      mins,
      secs,
    });
  }, [date]);

  const counter = timerStringInDigits(timer);
  const hasScannerEnabled = useMemo(() => {
    return !isWeb();
  }, []);

  const navigateToScanPage = useCallback(
    (storeMaster: number | undefined) => {
      navigation.navigate(ShopperScreens.ItemDetails, {
        screen: ItemDetailsScreen.ItemScanner,
        params: {
          orderItemId,
          orderItemIds: [orderItemId],
          storeIndex,
          runId: state?.runId,
          oldRunId: runId,
          quantity,
          storeMaster,
          isAdjacentOOS: true,
          conversationId,
          source: 'Substitute Item Detail Page',
        },
      });
    },
    [conversationId, orderItemId, quantity, runId, state?.runId, storeIndex],
  );

  const handleFoundItem = useCallback(
    (storeMaster: number | undefined) => {
      if (hasScannerEnabled) navigateToScanPage(storeMaster);
      // else pick api
    },
    [hasScannerEnabled, navigateToScanPage],
  );

  const handleAdjacentItemFound = useCallback(async () => {
    if (routeParams?.adjacentOOSId) {
      setLoadingItemFound(true);
      const isResponseSuccess = await adjacentOOSItemFound(APIV1Version.V100, {
        adjacentOOSId: routeParams?.adjacentOOSId,
      });
      if (isResponseSuccess)
        getAdjacentOOSItem(APIV1Version.V100, {
          adjacentOOSId: routeParams?.adjacentOOSId,
        });
      setLoadingItemFound(false);
      if (showDropModal) setShowDropModal(false);
    }
  }, [
    adjacentOOSItemFound,
    getAdjacentOOSItem,
    routeParams?.adjacentOOSId,
    showDropModal,
  ]);

  const handleLoadingData = useCallback((loading: boolean) => {
    setLoadingData(loading);
  }, []);

  useEffect(() => {
    if (date && initDaysToExpire > 0) {
      const startTimer = setInterval(() => {
        timerFunc();
      }, 1000);
      return () => {
        clearInterval(startTimer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    getConstants();
    if (routeParams?.runId) getRunForItemDetail(routeParams?.runId);
    if (routeParams?.adjacentOOSId) {
      getAdjacentOOSItem(APIV1Version.V100, {
        adjacentOOSId: routeParams?.adjacentOOSId,
      });
      appDispatch(homeActions.setAdjOOSIdsOpened(routeParams?.adjacentOOSId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeDropModal = useCallback(() => {
    setShowDropModal(false);
  }, [setShowDropModal]);

  const handleOpenDropModal = useCallback(() => {
    setShowDropModal(true);
  }, [setShowDropModal]);

  const navigateToBatchDetail = useCallback(() => {
    navigation.goBack();
  }, []);

  const fetchSnoozedItems = useCallback(async () => {
    const storedTimerData = await storage.getItem(StorageKeys.snoozeTimer);
    const existingSnooze = JSON.parse(storedTimerData || '{}');
    setSnoozedTimerItemsData(existingSnooze);
  }, []);

  useEffect(() => {
    fetchSnoozedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SubstituteItemDetailsContext.Provider value={provider}>
      <Header
        showDefaultLeftHeader={
          ((state?.constants?.ADJ_OOS_BACK_NAVIGATION === 'true' ||
            !isRequestSentToShopper) &&
            Boolean(state.itemStatus)) ||
          Boolean(snoozedTimerItemsData?.[routeParams?.adjacentOOSId ?? 0])
        }
        hideBottomSafeArea
        headerRightComponent={
          <If condition={state.item?.shopperCategory}>
            <LocationInStore category={state.item?.shopperCategory} />
          </If>
        }
        headerColor={theme?.background.blue[300]}>
        <Box style={styles.mainContainer}>
          <SubstituteItemDetailsBody
            setLoading={handleLoadingData}
            loading={constantsLoading}
            bottomButtonLoading={bottomButtonLoading}
            onButtonPress={handleFoundItem}
            buttonText={
              hasScannerEnabled ? button?.foundItem : button?.pickItem
            }
            isRequestAcceptedByBuyer={isRequestAcceptedByBuyer}
            subsDetailsBody={
              <If condition={isRequestSentToShopper && !loadingData}>
                <SubstituteDetails
                  aOOSData={state.aoosData}
                  handleAdjacentItemFound={handleAdjacentItemFound}
                  handleOpenDropModal={handleOpenDropModal}
                  isLoading={loadingItemFound}
                  storeLogo={state.item?.order?.run?.store?.storeLogoUrl}
                  oldQuantity={oldQuantity}
                  newQuantity={newQuantity}
                  adjacentOOSId={routeParams?.adjacentOOSId}
                />
              </If>
            }
          />
        </Box>
      </Header>
      <CustomerResponseSheet
        open={isFound && !isTimedOut && isRequestSentToBuyer}
        navigateToBatchDetail={navigateToBatchDetail}
        timer={counter}
        status={state.aoosData?.shopperStatus}
      />
      <DropAdjacentItemModal
        onClose={closeDropModal}
        orderItemId={routeParams?.orderItemId ?? 0}
        open={showDropModal}
        handleAdjacentItemFound={handleAdjacentItemFound}
        navigateToBatchDetail={navigateToBatchDetail}
      />
      <If condition={isRequestAcceptedByBuyer}>
        <BottomButton
          disabled={bottomButtonLoading || !quantity}
          showDividers={true}
          onPress={() => handleFoundItem(storeMasterId)}
          isLoading={bottomButtonLoading}
          loadingIndicator
          loadingText={button?.loading}
          title={hasScannerEnabled ? button?.foundItem : button?.pickItem}
          style={styles.buttonStyle}
          leftComponent={
            <If condition={hasScannerEnabled}>
              <QRCode viewBox="-5 -2 22 17" style={styles.iconBox} />
            </If>
          }
        />
      </If>
    </SubstituteItemDetailsContext.Provider>
  );
};

export default React.memo(SubstituteItemDetailsPage);
